import React, { useEffect, useRef, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  clearAuthSessionAction, getVendorProfileAction,verifyVendorTokenAction,
} from "../actions/auth";
import { getAuthToken } from "../utils/authStorage";

const getTokenExpiry = (token) => {
  try {
    const encodedPayload = token.split(".")[1];
    if (!encodedPayload) return null;
    const base64 = encodedPayload
      .replace(/-/g, "+")
      .replace(/_/g, "/")
      .padEnd(encodedPayload.length + ((4 - (encodedPayload.length % 4)) % 4), "=");
    const payload = JSON.parse(window.atob(base64));
    return Number.isFinite(Number(payload.exp)) ? Number(payload.exp) * 1000 : null;
  } catch {
    return null;
  }
};

export default function RequireAuth() {
  const dispatch = useDispatch();
  const isLogin = useSelector((state) => Boolean(state.auth?.isLogin));
  const isLoginRef = useRef(isLogin);
  isLoginRef.current = isLogin;
  const location = useLocation();
  const [verification, setVerification] = useState("checking");

  useEffect(() => {
    let active = true;
    let expiryTimer;
    setVerification("checking");

    const token = getAuthToken();
    if (!token) {
      dispatch(clearAuthSessionAction());
      setVerification("rejected");
      return () => {
        active = false;
      };
    }

    const allowCurrentToken = (result, tokenBeingVerified = token) => {
      if (!active) return;
      const currentToken = getAuthToken();
      if (!currentToken) {
        setVerification("rejected");
        return;
      }
      if (currentToken !== tokenBeingVerified) {
        setVerification("checking");
        dispatch(verifyVendorTokenAction())
          .then((nextResult) => allowCurrentToken(nextResult, currentToken))
          .catch(() => allowCurrentToken({ unavailable: true }, currentToken));
        return;
      }
      if (result?.invalidToken) {
        if (getAuthToken() === tokenBeingVerified) {
          dispatch(clearAuthSessionAction());
        }
        setVerification("rejected");
        return;
      }

      if (!result?.verified) {
        if (result?.unavailable && isLoginRef.current) {
          setVerification("verified");
          return;
        }
        setVerification("rejected");
        return;
      }

      const expiresAt = getTokenExpiry(tokenBeingVerified);
      const remainingTime = expiresAt === null ? null : expiresAt - Date.now();
      if (remainingTime !== null && remainingTime <= 0) {
        if (getAuthToken() === tokenBeingVerified) {
          dispatch(clearAuthSessionAction());
        }
        setVerification("rejected");
        return;
      }

      dispatch(getVendorProfileAction());
      setVerification("verified");
      if (remainingTime !== null) {
        const expireWhenDue = () => {
          if (!active || getAuthToken() !== tokenBeingVerified) return;
          const timeRemaining = expiresAt - Date.now();
          if (timeRemaining > 0) {
            expiryTimer = window.setTimeout(
              expireWhenDue,
              Math.min(timeRemaining, 2147483647),
            );
            return;
          }
          dispatch(clearAuthSessionAction());
          setVerification("rejected");
        };
        expiryTimer = window.setTimeout(
          expireWhenDue,
          Math.min(remainingTime, 2147483647),
        );
      }
    };

    dispatch(verifyVendorTokenAction())
      .then(allowCurrentToken)
      .catch(() => allowCurrentToken({ unavailable: true }));

    return () => {
      active = false;
      if (expiryTimer) window.clearTimeout(expiryTimer);
    };
  }, [dispatch]);

  if (verification === "checking") {
    return (
      <div className="route-auth-loading" role="status" aria-label="Loading">
        <span />
      </div>
    );
  }

  if (verification !== "verified") {
    return <Navigate to="/" replace state={{ from: location }} />;
  }

  return <Outlet />;
}
